import React from 'react';
import { useTranslation } from 'react-i18next';
import { format as formatDate, parseISO } from 'date-fns';
import { Ship, Wallet, Truck, CalendarOff, ArrowRight } from 'lucide-react';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartCard, EmptyChart } from '../charts/ChartPrimitives';
import {
  formatUnits,
  formatDaysOfCover,
  daysOfCoverBand,
  buyingActionBand,
  BUYING_ACTION_PRIORITY,
  RAG_TEXT,
  RAG_PILL,
} from '../../lib/reporting-format';
import { useOnOrderSummary, useReorderSignals } from '../../hooks/useReportingV2';
import { OnOrderReportDialog } from './OnOrderReportDialog';
import {
  buyingActionLabel,
  ETA_BUCKET_ORDER,
  type EtaBucketKey,
  type OnOrderSummary,
  type ReorderSignalRow,
} from './types';

interface StockOnOrderTabProps {
  organizationId: string | null;
  locationId: string | null;
  active: boolean;
}

const PREVIEW_COUNT = 8;

/** i18n-defaulted ETA bucket label. */
function etaBucketLabel(bucket: EtaBucketKey, t: ReturnType<typeof useTranslation>['t']): string {
  switch (bucket) {
    case 'overdue':
      return t('reporting.v2.eta.overdue', 'Overdue');
    case 'this_week':
      return t('reporting.v2.eta.thisWeek', 'This week');
    case 'next_30':
      return t('reporting.v2.eta.next30', 'Next 30 days');
    case 'later':
      return t('reporting.v2.eta.later', 'Later');
    default:
      return t('reporting.v2.eta.noEta', 'No ETA');
  }
}

function formatEta(iso: string | null): string {
  if (!iso) return '-';
  try {
    return formatDate(parseISO(iso), 'd MMM');
  } catch {
    return iso;
  }
}

/** One headline KPI — div, not a button (nothing to navigate to off-tab). */
const KpiTile: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
  detail?: string;
  attention?: boolean;
  loading?: boolean;
}> = ({ label, value, icon: Icon, detail, attention, loading }) => (
  <div className="flex flex-col rounded-lg border bg-card p-4">
    <div className="flex items-start justify-between gap-2">
      <p className="min-w-0 truncate text-xs font-medium text-muted-foreground">{label}</p>
      <Icon
        className={cn('h-4 w-4 shrink-0', attention ? 'text-amber-600' : 'text-muted-foreground/70')}
      />
    </div>
    {loading ? (
      <Skeleton className="mt-2 h-8 w-24" />
    ) : (
      <p className="mt-2 truncate text-2xl font-bold tabular-nums leading-none">{value}</p>
    )}
    {detail && !loading && <p className="mt-1 truncate text-xs text-muted-foreground">{detail}</p>}
  </div>
);

/**
 * On Order tab. A point-in-time snapshot of inbound stock (open POs): what's on
 * the water, its committed value, when it lands — plus the payoff: at-risk SKUs
 * reclassified against inbound into order-now / arriving-late / covered.
 * Location is inherited from the page nav. Transfers are excluded in v1 (they
 * surface in the incoming-shipments digest).
 */
export const StockOnOrderTab: React.FC<StockOnOrderTabProps> = ({
  organizationId,
  locationId,
  active,
}) => {
  const { t } = useTranslation('inventory');
  const { format, currency } = useCurrencyFormatter();
  const [reportOpen, setReportOpen] = React.useState(false);

  const onOrder = useOnOrderSummary({ organizationId, locationId, enabled: active });
  const reorder = useReorderSignals({ organizationId, locationId, enabled: active });

  const summary = onOrder.data as OnOrderSummary | undefined;
  const totals = summary?.totals;

  const buckets = React.useMemo(() => {
    const byKey = new Map<EtaBucketKey, { units: number; value: number }>();
    for (const b of summary?.etaBuckets ?? []) byKey.set(b.bucket, { units: b.units, value: b.value });
    return ETA_BUCKET_ORDER.map((key) => ({
      key,
      units: byKey.get(key)?.units ?? 0,
      value: byKey.get(key)?.value ?? 0,
    })).filter((b) => b.units > 0);
  }, [summary]);

  const signals = (reorder.data ?? []) as ReorderSignalRow[];
  const rankedSignals = React.useMemo(() => {
    return [...signals].sort((a, b) => {
      const byAction = BUYING_ACTION_PRIORITY[a.buyingAction] - BUYING_ACTION_PRIORITY[b.buyingAction];
      if (byAction !== 0) return byAction;
      const av = a.daysOfCover ?? Number.POSITIVE_INFINITY;
      const bv = b.daysOfCover ?? Number.POSITIVE_INFINITY;
      return av - bv;
    });
  }, [signals]);
  const orderNowCount = React.useMemo(
    () => signals.filter((s) => s.buyingAction === 'order_now').length,
    [signals],
  );

  const totalsLoading = onOrder.isLoading && !summary;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          label={t('reporting.v2.tile.onOrderUnits', 'Units on order')}
          value={formatUnits(totals?.onOrderUnits ?? 0)}
          icon={Truck}
          detail={t('reporting.v2.openPosOnly', 'Open purchase orders')}
          loading={totalsLoading}
        />
        <KpiTile
          label={t('reporting.v2.tile.committedValue', 'Committed value')}
          value={format(totals?.committedValue ?? 0, currency)}
          icon={Wallet}
          detail={t('reporting.v2.atPoCost', 'At PO landed cost')}
          loading={totalsLoading}
        />
        <KpiTile
          label={t('reporting.v2.tile.posInbound', 'POs inbound')}
          value={formatUnits(totals?.poCount ?? 0)}
          icon={Ship}
          detail={
            totals?.valueThisWeek
              ? t('reporting.v2.dueThisWeek', '{{v}} due this week', {
                  v: format(totals.valueThisWeek, currency),
                })
              : t('reporting.v2.openOrders', 'Open orders')
          }
          loading={totalsLoading}
        />
        <KpiTile
          label={t('reporting.v2.tile.missingEta', 'Missing ETAs')}
          value={formatUnits(totals?.missingEtaCount ?? 0)}
          icon={CalendarOff}
          attention={(totals?.missingEtaCount ?? 0) > 0}
          detail={t('reporting.v2.poNoEta', 'POs without a delivery date')}
          loading={totalsLoading}
        />
      </div>

      {/* Inbound by ETA */}
      <ChartCard
        title={t('reporting.v2.inboundByEta', 'Inbound by ETA')}
        description={t(
          'reporting.v2.inboundByEtaDesc',
          'Outstanding open-PO units and committed value, bucketed by expected arrival.',
        )}
      >
        {totalsLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : buckets.length === 0 ? (
          <EmptyChart
            message={t('reporting.v2.noInbound', 'No open purchase orders are currently inbound.')}
            className="min-h-[8rem]"
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="border-b">
                <th className="py-2 text-left font-medium">{t('reporting.v2.window', 'Window')}</th>
                <th className="py-2 text-right font-medium">{t('reporting.v2.units', 'Units')}</th>
                <th className="py-2 text-right font-medium">{t('reporting.v2.value', 'Value')}</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((b) => (
                <tr key={b.key} className="border-b last:border-0">
                  <td className="py-2.5">
                    <span
                      className={cn(
                        'font-medium',
                        b.key === 'overdue' && 'text-red-600',
                        b.key === 'no_eta' && 'text-muted-foreground',
                      )}
                    >
                      {etaBucketLabel(b.key, t)}
                    </span>
                  </td>
                  <td className="py-2.5 text-right tabular-nums">{formatUnits(b.units)}</td>
                  <td className="py-2.5 text-right tabular-nums">{format(b.value, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ChartCard>

      {/* Buying signals — reorder list reclassified against inbound */}
      <ChartCard
        title={t('reporting.v2.buyingSignals', 'Buying signals')}
        description={t(
          'reporting.v2.buyingSignalsDesc',
          'At-risk SKUs cross-referenced against inbound stock: order now vs already covered.',
        )}
        action={
          signals.length > 0 ? (
            <button
              type="button"
              onClick={() => setReportOpen(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-threadheads-purple hover:underline"
            >
              {t('reporting.v2.openFullReport', 'Open full report')}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : undefined
        }
      >
        {reorder.isLoading && signals.length === 0 ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : signals.length === 0 ? (
          <EmptyChart
            message={t(
              'reporting.v2.noSignals',
              'Nothing needs reordering right now. Every tracked SKU is above its threshold.',
            )}
            className="min-h-[8rem]"
          />
        ) : (
          <>
            {orderNowCount > 0 && (
              <p className="mb-3 text-xs text-muted-foreground">
                {t('reporting.v2.orderNowSummary', '{{n}} need ordering now; the rest have stock inbound.', {
                  n: orderNowCount,
                })}
              </p>
            )}
            <ul className="divide-y">
              {rankedSignals.slice(0, PREVIEW_COUNT).map((s) => {
                const coverBand = daysOfCoverBand(s.daysOfCover);
                const actionBand = buyingActionBand(s.buyingAction);
                return (
                  <li key={`${s.skuId}-${s.locationId}`} className="flex items-center justify-between gap-4 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm font-medium">{s.sku}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {s.category}
                        {' · '}
                        {s.productType}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-4 text-right">
                      <div className="hidden sm:block">
                        <p className="text-xs text-muted-foreground">{t('reporting.v2.cover', 'Cover')}</p>
                        <p className={cn('text-sm font-semibold tabular-nums', RAG_TEXT[coverBand])}>
                          {formatDaysOfCover(s.daysOfCover)}
                        </p>
                      </div>
                      <div className="hidden w-16 md:block">
                        <p className="text-xs text-muted-foreground">{t('reporting.v2.incoming', 'Incoming')}</p>
                        <p className="text-sm font-semibold tabular-nums">
                          {s.incoming > 0 ? `+${formatUnits(s.incoming)}` : '-'}
                        </p>
                      </div>
                      <div className="hidden w-20 md:block">
                        <p className="text-xs text-muted-foreground">{t('reporting.v2.nextEta', 'Next ETA')}</p>
                        <p className="text-sm tabular-nums text-muted-foreground">{formatEta(s.nextEtaDate)}</p>
                      </div>
                      <span
                        className={cn(
                          'inline-flex w-28 justify-center whitespace-nowrap rounded-md px-2 py-1 text-xs font-semibold',
                          RAG_PILL[actionBand],
                        )}
                      >
                        {buyingActionLabel(s.buyingAction, t)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </ChartCard>

      <OnOrderReportDialog open={reportOpen} onOpenChange={setReportOpen} rows={rankedSignals} />
    </div>
  );
};
